import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ChevronDown, ChevronRight, BookOpen, ArrowLeft } from 'lucide-react';

// ─── Shared content block renderer used by all public doc pages ──────────────

export const DocBlock = ({ block }) => {
  switch (block.type) {
    case 'paragraph':
      return <p className="text-gray-600 leading-relaxed mb-3">{block.text}</p>;

    case 'subheading':
      return <h4 className="font-semibold text-[#1A2E6E] mt-4 mb-2 text-base">{block.text}</h4>;

    case 'note':
      return (
        <div className="bg-blue-50 border-l-4 border-blue-400 rounded px-4 py-2 mb-3 text-sm text-blue-800">
          {block.text}
        </div>
      );

    case 'list':
      return (
        <ul className="list-disc list-inside space-y-1 mb-3 text-gray-600">
          {block.items.map((item, i) => (
            <li key={i} className="leading-relaxed">{item}</li>
          ))}
        </ul>
      );

    case 'table':
      return (
        <div className="overflow-x-auto mb-3">
          <table className="w-full text-sm border border-gray-200 rounded-lg overflow-hidden">
            <thead className="bg-gray-50">
              <tr>
                {block.headers.map((h, i) => (
                  <th key={i} className="px-4 py-2 text-left font-semibold text-[#1A2E6E] border-b border-gray-200">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {block.rows.map((row, ri) => (
                <tr key={ri} className={ri % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                  {row.map((cell, ci) => (
                    <td key={ci} className="px-4 py-2 text-gray-600 border-b border-gray-200">{cell}</td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );

    case 'faq':
      return (
        <div className="space-y-3 mb-3">
          {block.items.map((item, i) => (
            <div key={i} className="border border-gray-200 rounded-lg p-4">
              <p className="font-semibold text-[#1A2E6E] mb-1">Q: {item.q}</p>
              <p className="text-gray-600 text-sm leading-relaxed">A: {item.a}</p>
            </div>
          ))}
        </div>
      );

    default:
      return null;
  }
};

// ─── Accordion section ───────────────────────────────────────────────────────

export const AccordionSection = ({ section }) => {
  const [open, setOpen] = useState(false);

  return (
    <div id={section.id} className="border border-gray-200 rounded-xl overflow-hidden mb-3">
      <button
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between px-6 py-4 text-left hover:bg-gray-50 transition-colors"
      >
        <span className="font-semibold text-[#1A2E6E]">{section.title}</span>
        {open ? (
          <ChevronDown size={18} className="text-gray-400 flex-shrink-0" />
        ) : (
          <ChevronRight size={18} className="text-gray-400 flex-shrink-0" />
        )}
      </button>
      {open && (
        <div className="px-6 pb-5 pt-1 border-t border-gray-200 bg-white">
          {section.content.map((block, i) => (
            <DocBlock key={i} block={block} />
          ))}
        </div>
      )}
    </div>
  );
};

// ─── Public docs page shell ──────────────────────────────────────────────────

export const DocsPageShell = ({ title, subtitle, sections, navLinks }) => {
  return (
    <div className="min-h-screen bg-[#F8F9FC]">
      {/* Hero */}
      <div className="bg-[#1A2E6E] text-white py-14 px-4">
        <div className="max-w-4xl mx-auto">
          {/* F7: these doc pages render outside PublicLayout (no site header/footer),
              so anyone arriving via a shared link needs an explicit way back. */}
          <Link
            to="/"
            className="inline-flex items-center gap-1.5 text-sm text-white/70 hover:text-white transition-colors mb-5"
          >
            <ArrowLeft size={16} /> Back to site
          </Link>
          <div className="flex items-center gap-3 mb-2">
            <BookOpen size={28} />
            <h1 className="text-3xl font-bold">{title}</h1>
          </div>
          <p className="text-white/70 mt-1">{subtitle}</p>
          {navLinks && (
            <div className="flex gap-4 mt-5 flex-wrap">
              {navLinks.map((l) => (
                <Link
                  key={l.to}
                  to={l.to}
                  className="px-4 py-2 rounded-lg bg-white/10 hover:bg-white/20 text-sm font-medium transition-colors"
                >
                  {l.label}
                </Link>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 py-10">
        {sections.map((section) => (
          <AccordionSection key={section.id} section={section} />
        ))}
      </div>
    </div>
  );
};
